// src/context/LanguageContext.jsx

import React, {createContext, useContext, useState,useEffect, useCallback} from "react";

import i18n from "../i18n/i18n";

import { getLanguage, changeLanguage} from "../services/languageService";

//=========================================================
// Contexte
//=========================================================

const LanguageContext = createContext(null);

//=========================================================
// Hook
//=========================================================

export const useLanguage = () => {

    const context = useContext(LanguageContext);

    if (!context) {

        throw new Error(
            "useLanguage doit être utilisé dans LanguageProvider."
        );

    }

    return context;

};

//=========================================================
// Provider
//=========================================================

export function LanguageProvider({ children }) {

    //-----------------------------------------------------
    // Etats
    //-----------------------------------------------------

    const [language, setLanguage] = useState("FR");

    const [loading, setLoading] = useState(true);

    //-----------------------------------------------------
    // Appliquer une langue
    //-----------------------------------------------------

    const applyLanguage = useCallback(async (lang) => {

        const current = (lang || "FR").toUpperCase();

        //-------------------------------------------------
        // Etat React
        //-------------------------------------------------

        setLanguage(current);

        //-------------------------------------------------
        // i18next
        //-------------------------------------------------

        await i18n.changeLanguage(current.toLowerCase());

        //-------------------------------------------------
        // HTML
        //-------------------------------------------------

        document.documentElement.lang = current.toLowerCase();

        document.documentElement.dir =
            current === "AR"
                ? "rtl"
                : "ltr";

        //-------------------------------------------------
        // CSS
        //-------------------------------------------------

        document.body.classList.remove(

            "lang-fr",
            "lang-en",
            "lang-ar"

        );

        document.body.classList.add(

            `lang-${current.toLowerCase()}`

        );

    }, []);

    //-----------------------------------------------------
    // Chargement initial
    //-----------------------------------------------------

    const loadLanguage = useCallback(async () => {

        try {

            const lang = getLanguage();

            await applyLanguage(lang);

        }
        catch (error) {

            console.error(
                "Erreur chargement langue :",
                error
            );

            await applyLanguage("FR");

        }
        finally {

            setLoading(false);

        }

    }, [applyLanguage]);

    //-----------------------------------------------------
    // Initialisation
    //-----------------------------------------------------

    useEffect(() => {

        loadLanguage();

    }, [loadLanguage]);

    //-----------------------------------------------------
    // Changement de langue
    //-----------------------------------------------------

    const updateLanguage = async (userId, lang) => {

        if (!lang) {

            return;

        }

        const current = lang.toUpperCase();

        //-------------------------------------------------
        // Déjà sélectionnée
        //-------------------------------------------------

        if (current === language) {

            return;

        }

        //-------------------------------------------------
        // Mise à jour immédiate de l'interface
        //-------------------------------------------------

        await applyLanguage(current);

        //-------------------------------------------------
        // Sauvegarde serveur
        //-------------------------------------------------

        try {

            await changeLanguage(userId, current);

        }
        catch (error) {

            console.error(
                "Erreur sauvegarde langue :",
                error
            );

        }

    };

    //-----------------------------------------------------
    // Valeur du contexte
    //-----------------------------------------------------

    const value = {

        language,

        loading,

        changeLanguage: updateLanguage,

        isFrench: language === "FR",

        isArabic: language === "AR",

        isEnglish: language === "EN"

    };

    //-----------------------------------------------------

    return (

        <LanguageContext.Provider value={value}>

            {children}

        </LanguageContext.Provider>

    );

}

export default LanguageContext;